<template>
  <div class="company-introduction">
    <!-- 标题区域 -->
    <div class="section-header">
      <h2 class="section-title">{{ companyData.title }}</h2>
      <p class="section-subtitle">{{ companyData.subtitle }}</p>
      <div class="title-decoration"></div>
    </div>

    <!-- 公司信息卡片 -->
    <div class="company-card">
      <div class="company-content">
        <!-- 公司基本信息 -->
        <div class="company-basic">
          <div class="company-logo">
            <!-- <i class="el-icon-office-building"></i> -->
             <img src="@/assets/img/home/aurelia_logo.png" alt="" class="company-logo-img">
          </div>
        <div class="company-info">
          <h3 class="company-name">{{ companyData.name }}</h3>
          <p class="company-establish">{{ companyData.establishYear }}</p>
        </div>
        </div>

        <!-- 公司描述 -->
        <div class="company-description">
          <p class="description-text">
            {{ companyData.description }}
          </p>
        </div>

        <!-- 合作伙伴 -->
        <div class="partners-section">
          <h4 class="partners-title">{{ $t('company.introduction.partners.title') }}</h4>
          <p class="partners-description">
            {{ companyData.partners.description }}
          </p>
          <div class="partners-logos">
            <div class="partner-logo" v-for="partner in companyData.partners.companies" :key="partner">
              <span class="partner-name">{{ partner }}</span>
            </div>
          </div>
        </div>

        <!-- 服务优势 -->
        <div class="advantages-section">
          <h4 class="advantages-title">{{ $t('company.introduction.advantages.title') }}</h4>
          <div class="advantages-grid">
            <div class="advantages-column" v-for="(column, columnIndex) in advantagesColumns" :key="columnIndex">
              <div class="advantage-item" v-for="advantage in column" :key="advantage.title">
                <div class="advantage-icon" :style="{ backgroundColor: advantage.color }">
                  <i :class="advantage.icon"></i>
                </div>
                <div class="advantage-content">
                  <h5 class="advantage-title">{{ advantage.title }}</h5>
                  <p class="advantage-description">{{ advantage.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 服务范围 -->
        <div class="services-section">
          <h4 class="services-title">{{ $t('company.introduction.services.title') }}</h4>
          <div class="services-grid">
            <div class="services-column" v-for="(column, columnIndex) in servicesColumns" :key="columnIndex">
              <div class="service-category" v-for="service in column" :key="service.category">
                <h5 class="service-category-title">{{ service.category }}</h5>
                <div class="service-items">
                  <span class="service-item" v-for="item in service.items" :key="item">{{ item }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSectionTypeContent } from '../../api/system'

export default {
  name: 'CompanyIntroduction',
  data() {
    return {
      // 公司数据 - 从后端接口获取
      companyData: {
        title: '',
        subtitle: '',
        name: '',
        establishYear: '',
        description: '',
        partners: {
          description: '',
          companies: []
        },
        advantages: [],
        services: []
      },
      // 加载状态
      loading: false
    }
  },
  computed: {
    advantagesColumns() {
      // 将优势项分成两列，每列两个
      const advantages = this.companyData.advantages.length > 0 ? this.companyData.advantages : [
        {
          title: this.$t('company.introduction.advantages.experiencedTeam'),
          description: this.$t('company.introduction.advantages.experiencedTeamDesc'),
          icon: 'el-icon-user-solid',
          color: '#4CAF50'
        },
        {
          title: this.$t('company.introduction.advantages.capacityControl'),
          description: this.$t('company.introduction.advantages.capacityControlDesc'),
          icon: 'el-icon-ship',
          color: '#2196F3'
        },
        {
          title: this.$t('company.introduction.advantages.agentNetwork'),
          description: this.$t('company.introduction.advantages.agentNetworkDesc'),
          icon: 'el-icon-connection',
          color: '#FF9800'
        },
        {
          title: this.$t('company.introduction.advantages.routeArrangement'),
          description: this.$t('company.introduction.advantages.routeArrangementDesc'),
          icon: 'el-icon-map-location',
          color: '#9C27B0'
        }
      ];
      const mid = Math.ceil(advantages.length / 2);
      return [
        advantages.slice(0, mid),
        advantages.slice(mid)
      ];
    },
    servicesColumns() {
      // 将服务类别分成两列，每列两个
      const services = this.companyData.services.length > 0 ? this.companyData.services : [
        {
          category: this.$t('company.introduction.services.transportation'),
          items: this.$t('company.introduction.services.transportationItems')
        },
        {
          category: this.$t('company.introduction.services.supporting'),
          items: this.$t('company.introduction.services.supportingItems')
        },
        {
          category: this.$t('company.introduction.services.extended'),
          items: this.$t('company.introduction.services.extendedItems')
        },
        {
          category: this.$t('company.introduction.services.network'),
          items: this.$t('company.introduction.services.networkItems')
        }
      ];
      const mid = Math.ceil(services.length / 2);
      return [
        services.slice(0, mid),
        services.slice(mid)
      ];
    }
  },
  methods: {
    // 从后端获取公司信息的方法
    async fetchCompanyInfo() {
      try {
        this.loading = true;
        
        // 调用后端API获取公司介绍数据
        const language = this.$i18n.locale || 'zh-CN';
        const response = await getSectionTypeContent('company_intro', language);
        console.log('公司介绍API响应:', response);
        
        if (response.data && response.data.success && response.data.data) {
          this.processCompanyData(response.data.data);
        } else {
          console.warn('API did not return company intro data, using fallback data.');
          this.useFallbackData();
        }
        
      } catch (error) {
        console.error('获取公司信息失败:', error);
        // 使用降级数据
        this.useFallbackData();
      } finally {
        this.loading = false;
      }
    },
    
    // 处理从API获取的公司数据
    processCompanyData(apiData) {
      // 将API返回的数据映射到组件数据结构
      const processedData = {
        title: '',
        subtitle: '',
        name: '',
        establishYear: '',
        description: '',
        partners: {
          description: '',
          companies: []
        },
        advantages: [],
        services: []
      };
      
      // 根据section_key映射数据
      apiData.forEach(item => {
        switch(item.sectionKey) {
          case 'company_basic':
            processedData.title = item.title || '';
            processedData.subtitle = item.subtitle || '';
            processedData.description = item.content || '';
            break;
          case 'company_name':
            processedData.name = item.title || '';
            break;
          case 'company_establish':
            processedData.establishYear = item.title || '';
            break;
          case 'company_description':
            processedData.description = item.title || '';
            break;
          case 'partners_description':
            processedData.partners.description = item.title || '';
            break;
          case 'partners_companies':
            // 合作伙伴公司列表，如果有的话
            if (item.title) {
              processedData.partners.companies = item.title.split(',').map(company => company.trim());
            }
            break;
          case 'company_advantages':
            if (item.title) {
              processedData.advantages.push({
                title: item.title,
                description: item.content || '',
                icon: item.icon || '',
                color: item.color || ''
              });
            }
            break;
          case 'company_services':
            if (item.title) {
              processedData.services.push({
                category: item.title,
                items: item.content.split(',').map(item => item.trim()) || []
              });
            }
            break;
        }
      });
      
      // 如果没有从API获取到数据，使用默认值
      if (!processedData.title) processedData.title = this.$t('company.introduction.title');
      if (!processedData.subtitle) processedData.subtitle = this.$t('company.introduction.subtitle');
      if (!processedData.name) processedData.name = this.$t('company.introduction.name');
      if (!processedData.establishYear) processedData.establishYear = '2013';
      if (!processedData.description) processedData.description = this.$t('company.introduction.description');
      if (!processedData.partners.description) processedData.partners.description = this.$t('company.introduction.partners.description');
      if (processedData.partners.companies.length === 0) {
        processedData.partners.companies = [
          'CMA', 'MAERSK', 'MSC', 'COSCO', 'HAPAG-LLOYD',
          'EVERGREEN', 'WHL', 'PIL', 'OOCL', 'ONE', 'YML', 'ZIM'
        ];
      }
      
      this.companyData = processedData;
    },
    
    // 使用降级数据
    useFallbackData() {
      this.companyData = {
        title: this.$t('company.introduction.title'),
        subtitle: this.$t('company.introduction.subtitle'),
        name: this.$t('company.introduction.name'),
        establishYear: '2013',
        description: this.$t('company.introduction.description'),
        partners: {
          description: this.$t('company.introduction.partners.description'),
          companies: [
            'CMA', 'MAERSK', 'MSC', 'COSCO', 'HAPAG-LLOYD', 
            'EVERGREEN', 'WHL', 'PIL', 'OOCL', 'ONE', 'YML', 'ZIM'
          ]
        },
        advantages: [],
        services: []
      };
    }
  },
  mounted() {
    // 组件挂载时获取数据
    this.fetchCompanyInfo();
    
    // 监听语言变化
    this.$watch('$i18n.locale', () => {
      this.fetchCompanyInfo();
    });
  }
}
</script>

<style lang="scss" scoped>
.company-introduction {
  padding: 120px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('../../assets/img/home/bg_01.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.5;
    z-index: 0;
    filter: brightness(1.1) contrast(1.1);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e0e0e0, transparent);
    z-index: 1;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  z-index: 1;
  
  .section-title {
    font-size: 3rem;
    font-weight: 700;
    background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 20px;
    position: relative;
    letter-spacing: -0.8px;
    
    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }
  
  .section-subtitle {
    font-size: 1.2rem;
    color: #64748b;
    margin-bottom: 40px;
    font-weight: 400;
    line-height: 1.7;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .title-decoration {
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #1a73e8, #34d399);
    margin: 0 auto;
    border-radius: 2px;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 12px;
      height: 12px;
      background: #1a73e8;
      border-radius: 50%;
      box-shadow: 0 0 0 4px rgba(26, 115, 232, 0.1);
    }
  }
}

.company-card {
  max-width: 1100px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  padding: 60px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.05),
    0 8px 25px rgba(0, 0, 0, 0.03),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.8);
  position: relative;
  z-index: 1;
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%);
    border-radius: 20px;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    padding: 40px 25px;
    margin: 0 20px;
    border-radius: 16px;
  }
}

.company-content {
  .company-basic {
    display: flex;
    align-items: center;
    margin-bottom: 50px;
    padding-bottom: 40px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    
    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
    }
    
    .company-logo {
      width: 100px;
      height: 100px;
      // background: linear-gradient(135deg, #e67e7e 0%, #02126c 100%);
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 2.5rem;
      margin-right: 30px;
      box-shadow: 
        0 8px 25px rgba(26, 115, 232, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
      position: relative;
      overflow: hidden;
      
      &::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
        transform: rotate(45deg);
      }
      
      @media (max-width: 768px) {
        margin-right: 0;
        margin-bottom: 25px;
        width: 90px;
        height: 90px;
        font-size: 2.2rem;
      }
      .company-logo-img {
        width: 95%;
        height: 95%;
        object-fit: contain;
      }
    }
    
    .company-info {
      .company-name {
        font-size: 2.2rem;
        font-weight: 700;
        background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        margin-bottom: 12px;
        letter-spacing: -0.4px;
        
        @media (max-width: 768px) {
          font-size: 1.8rem;
        }
      }
      
      .company-establish {
        font-size: 1.1rem;
        color: #64748b;
        font-weight: 500;
        position: relative;
        padding-left: 20px;
        
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 12px;
          height: 2px;
          background: #1a73e8;
          border-radius: 1px;
        }
      }
    }
  }
  
  .company-description {
    margin-bottom: 50px;
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    
    .description-text {
      font-size: 1.1rem;
      line-height: 1.9;
      color: #475569;
      text-align: justify;
      font-weight: 400;
      position: relative;
      padding: 25px;
      background: linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%);
      border-radius: 12px;
      border-left: 4px solid #1a73e8;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
      z-index: 1;
      
      &::before {
        content: '"';
        position: absolute;
        top: 10px;
        left: 15px;
        font-size: 3rem;
        color: #1a73e8;
        opacity: 0.2;
        font-family: serif;
      }
    }
  }
  
  .partners-section {
    margin-bottom: 50px;
    padding: 40px;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.8);
    box-shadow: 
      0 8px 25px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    
    .partners-title {
      font-size: 1.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 20px;
      text-align: center;
      letter-spacing: -0.3px;
    }
    
    .partners-description {
      font-size: 1.05rem;
      color: #64748b;
      text-align: center;
      margin-bottom: 30px;
      line-height: 1.7;
      font-weight: 400;
    }
    
    .partners-logos {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 15px;
      
      .partner-logo {
        background: white;
        padding: 12px 20px;
        border-radius: 10px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
        
        .partner-name {
          font-size: 0.95rem;
          font-weight: 600;
          color: #334155;
          letter-spacing: 0.5px;
        }
        
        &:hover {
          border-color: #1a73e8;
          background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
          transform: translateY(-2px);
          box-shadow: 
            0 4px 15px rgba(26, 115, 232, 0.1),
            0 2px 8px rgba(0, 0, 0, 0.05);
        }
      }
    }
  }
  
  .advantages-section {
    margin-bottom: 50px;
    
    .advantages-title {
      font-size: 1.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 35px;
      text-align: center;
      letter-spacing: -0.3px;
    }
    
    .advantages-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      
      @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 25px;
      }
    }
    
    .advantages-column {
      display: flex;
      flex-direction: column;
      gap: 25px;
    }
    
    .advantage-item {
      display: flex;
      align-items: flex-start;
      padding: 25px;
      background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
      border-radius: 16px;
      transition: all 0.4s ease;
      border: 1px solid rgba(255, 255, 255, 0.8);
      box-shadow: 
        0 4px 15px rgba(0, 0, 0, 0.03),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
      position: relative;
      overflow: hidden;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #1a73e8, #34d399);
      }
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 
          0 15px 35px rgba(0, 0, 0, 0.08),
          0 5px 15px rgba(0, 0, 0, 0.03);
      }
      
      .advantage-icon {
        width: 60px;
        height: 60px;
        border-radius: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 1.5rem;
        margin-right: 20px;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        position: relative;
        overflow: hidden;
        
        &::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transform: rotate(45deg);
        }
      }
      
      .advantage-content {
        .advantage-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 12px;
          letter-spacing: -0.2px;
        }
        
        .advantage-description {
          font-size: 0.95rem;
          color: #64748b;
          line-height: 1.6;
          font-weight: 400;
        }
      }
    }
  }
  
    .services-section {
      margin-bottom: 0;
      
      .services-title {
        font-size: 1.5rem;
        font-weight: 700;
        background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        margin-bottom: 35px;
        text-align: center;
        letter-spacing: -0.3px;
      }
      
      .services-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-auto-rows: 1fr; /* 确保所有行高度相同 */
        gap: 40px;
        
        @media (max-width: 768px) {
          grid-template-columns: 1fr;
          gap: 25px;
        }
      }
      
      .services-column {
        display: flex;
        flex-direction: column;
        gap: 25px;
      }
      
      .service-category {
        background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
        padding: 30px;
        border-radius: 16px;
        border: 1px solid rgba(255, 255, 255, 0.8);
        box-shadow: 
          0 8px 25px rgba(0, 0, 0, 0.04),
          inset 0 1px 0 rgba(255, 255, 255, 0.8);
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
        height: 100%; /* 确保卡片填满容器高度 */
        display: flex;
        flex-direction: column;
        
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #1a73e8, #34d399);
        }
        
        &:hover {
          transform: translateY(-3px);
          box-shadow: 
            0 12px 30px rgba(0, 0, 0, 0.08),
            0 4px 12px rgba(0, 0, 0, 0.03);
        }
        
        .service-category-title {
          font-size: 1.3rem;
          font-weight: 700;
          background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 20px;
          text-align: center;
          letter-spacing: -0.2px;
          flex-shrink: 0; /* 防止标题被压缩 */
        }
        
        .service-items {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: center;
          flex-grow: 1; /* 让服务项区域填满剩余空间 */
          align-content: flex-start; /* 顶部对齐服务项 */
          
          .service-item {
            background: white;
            padding: 8px 16px;
            border-radius: 8px;
            border: 1px solid rgba(0, 0, 0, 0.08);
            font-size: 0.9rem;
            font-weight: 500;
            color: #334155;
            transition: all 0.2s ease;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
            
            &:hover {
              border-color: #1a73e8;
              background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
              transform: translateY(-1px);
              box-shadow: 
                0 3px 10px rgba(26, 115, 232, 0.1),
                0 1px 4px rgba(0, 0, 0, 0.05);
            }
          }
        }
      }
    }
}

// 响应式设计
@media (max-width: 768px) {
  .company-introduction {
    padding: 60px 0;
  }
  
  .company-card {
    padding: 30px 20px;
  }
  
  .partners-logos {
    .partner-logo {
      padding: 6px 12px;
      
      .partner-name {
        font-size: 0.8rem;
      }
    }
  }
}

// 动画效果
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.company-card {
  animation: fadeInUp 0.6s ease forwards;
}
</style>
